import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { fetchTour, fetchTours } from "../lib/api";
import Gallery from "../components/Gallery";
import BookingDialog from "../components/BookingDialog";
import TourCard from "../components/TourCard";
import SEO from "../components/SEO";
import { useSettings } from "../lib/settings";
import { buildWhatsAppMessageUrl } from "../lib/whatsapp";
import { fireGoogleAdsConversion } from "../lib/googleAds";
import { findPrivateTier } from "../lib/utils";
import { useAutoTranslate, T } from "../lib/autoTranslate";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  Clock,
  Users,
  MapPin,
  Calendar,
  Check,
  X as XIcon,
  ArrowLeft,
  Loader2,
  ShieldCheck,
  Zap,
  MessageCircle,
} from "lucide-react";

const TourDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [tour, setTour] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const isPrivateOnly = tour?.category === "private";
  const translatedTitle = useAutoTranslate(tour?.title || "");
  const translatedDescription = useAutoTranslate(tour?.description || "");

  useEffect(() => {
    setLoading(true);
    fetchTour(id)
      .then(async (t) => {
        setTour(t);
        try {
          if (t.related_tour_ids && t.related_tour_ids.length > 0) {
            const all = await fetchTours();
            const picked = t.related_tour_ids
              .map((rid) => all.find((x) => x.id === rid))
              .filter(Boolean)
              .slice(0, 3);
            setRelated(picked);
          } else {
            const list = await fetchTours(t.category);
            setRelated(list.filter((x) => x.id !== t.id).slice(0, 3));
          }
        } catch (e) {
          setRelated([]);
        }
      })
      .catch(() => setTour(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="animate-spin text-[var(--ri-red)]" size={32} />
      </div>
    );
  }
  if (!tour) {
    return (
      <div className="container-x section text-center">
        <h2 className="font-serif-display text-[32px]">Tour not found</h2>
        <Link to="/" className="btn-outline mt-6 inline-block">Back home</Link>
      </div>
    );
  }

  const images = (tour.images && tour.images.length > 0) ? tour.images : [tour.image];
  const includes = tour.includes && tour.includes.length ? tour.includes : (tour.features || []);

  const seoTitle = tour.seo_title || `${tour.title} · ${settings?.seo_org_name || "Free Istanbul Tours"}`;
  const seoDescription = tour.seo_description || tour.description || `Book ${tour.title} in Istanbul. ${tour.duration}, ${tour.max_guests}.`;
  const seoImage = tour.seo_og_image || tour.image;
  const priceValue = tour.price_number || (tour.price || "").replace(/[^\d.]/g, "");
  const rating = settings?.review_average || "4.95";
  const reviewCount = settings?.review_count || 7030;
  const orgName = settings?.seo_org_name || "Free Istanbul Tours";

  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: tour.title,
    description: seoDescription,
    image: images,
    brand: { "@type": "Brand", name: orgName },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: String(rating),
      reviewCount: String(reviewCount),
      bestRating: "5",
    },
    offers: {
      "@type": "Offer",
      price: priceValue || undefined,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: typeof window !== "undefined" ? window.location.href : undefined,
    },
  };
  const touristLd = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: tour.title,
    description: seoDescription,
    image: images,
    touristType: tour.category,
    itinerary: {
      "@type": "ItemList",
      itemListElement: [
        { "@type": "Place", name: tour.location || "Istanbul", address: { "@type": "PostalAddress", addressLocality: "Istanbul", addressCountry: "TR" } },
      ],
    },
    provider: { "@type": "TravelAgency", name: orgName },
    offers: {
      "@type": "Offer",
      price: priceValue || undefined,
      priceCurrency: "EUR",
    },
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: (typeof window !== "undefined" ? window.location.origin : "") + "/" },
      { "@type": "ListItem", position: 2, name: tour.category, item: (typeof window !== "undefined" ? window.location.origin : "") + `/tours/${tour.category}` },
      { "@type": "ListItem", position: 3, name: tour.title },
    ],
  };

  return (
    <div>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={tour.seo_keywords}
        image={seoImage}
        path={`/tour/${tour.slug || tour.id}`}
        type="product"
        jsonLd={[productLd, touristLd, breadcrumbLd]}
      />
      <div className="container-x pt-8 pb-4">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-[13px] text-[var(--ri-ink-soft)] hover:text-[var(--ri-ink)]">
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      <section className="container-x pb-8">
        <div className="grid lg:grid-cols-[1fr_400px] gap-10">
          <div>
            {tour.label && (
              <span className="eyebrow text-[var(--ri-red)]">{tour.label}</span>
            )}
            <h1 className="font-serif-display text-[36px] md:text-[48px] leading-[1.05] mt-2">
              {translatedTitle}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-[14px] text-[var(--ri-ink-soft)]">
              <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[var(--ri-red)]" /> {tour.location}</span>
              <span className="flex items-center gap-1.5"><Clock size={14} className="text-[var(--ri-red)]" /> {tour.duration}</span>
              <span className="flex items-center gap-1.5"><Users size={14} className="text-[var(--ri-red)]" /> {tour.max_guests}</span>
              <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[var(--ri-red)]" /> {tour.schedule}</span>
            </div>

            <div className="mt-6">
              <Gallery images={images} title={tour.title} />
            </div>

            {tour.content_sections && tour.content_sections.length > 0 ? (
              <div className="mt-10 max-w-[720px] space-y-10">
                {tour.content_sections.map((sec, i) => (
                  <div key={i}>
                    {sec.title && <h2 className="font-serif-display text-[26px] md:text-[32px] leading-tight"><T>{sec.title}</T></h2>}
                    <div className="mt-4 space-y-4 text-[16px] leading-[1.8] text-[var(--ri-ink-soft)] rte-content">
                      {/<[a-z][\s\S]*>/i.test(sec.text || "") ? (
                        <div dangerouslySetInnerHTML={{ __html: sec.text }} />
                      ) : (
                        (sec.text || "").split(/\n\s*\n/).filter(Boolean).map((para, j) => (
                          <p key={j}><T>{para}</T></p>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-10 max-w-[720px]">
                <h2 className="font-serif-display text-[26px] md:text-[32px] leading-tight"><T>About this tour</T></h2>
                <div className="mt-4 space-y-4 text-[16px] leading-[1.8] text-[var(--ri-ink-soft)] rte-content">
                  <p>{translatedDescription}</p>
                  {tour.long_description && (
                    /<[a-z][\s\S]*>/i.test(tour.long_description) ? (
                      <div dangerouslySetInnerHTML={{ __html: tour.long_description }} />
                    ) : (
                      <p><T>{tour.long_description}</T></p>
                    )
                  )}
                </div>
              </div>
            )}

            {includes.length > 0 && (
              <div className="mt-10 grid sm:grid-cols-2 gap-6 max-w-[720px]">
                <div>
                  <h3 className="font-serif-display text-[20px] mb-3"><T>What's included</T></h3>
                  <ul className="space-y-2">
                    {includes.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[14.5px] text-[var(--ri-ink-soft)]">
                        <Check size={16} className="text-[var(--ri-red)] mt-0.5 shrink-0" />
                        <span><T>{f}</T></span>
                      </li>
                    ))}
                  </ul>
                </div>
                {tour.excludes && tour.excludes.length > 0 && (
                  <div>
                    <h3 className="font-serif-display text-[20px] mb-3"><T>Not included</T></h3>
                    <ul className="space-y-2">
                      {tour.excludes.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-[14.5px] text-[var(--ri-ink-soft)]">
                          <XIcon size={16} className="text-[var(--ri-muted)] mt-0.5 shrink-0" />
                          <span><T>{f}</T></span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {tour.meeting_point && (
              <div className="mt-10 max-w-[720px] rounded-2xl bg-[#fffdf7] border border-[var(--ri-border)] p-6">
                <p className="eyebrow text-[var(--ri-muted)] mb-2">Meeting point</p>
                <p className="text-[15px] text-[var(--ri-ink-soft)] leading-[1.7]">{tour.meeting_point}</p>
              </div>
            )}

            {tour.faqs && tour.faqs.length > 0 && (
              <div className="mt-10 max-w-[720px]">
                <h2 className="font-serif-display text-[26px] md:text-[32px] leading-tight mb-4">
                  Frequently asked questions
                </h2>
                <Accordion type="single" collapsible className="w-full">
                  {tour.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`item-${i}`} className="border-b border-[var(--ri-border)]">
                      <AccordionTrigger className="text-left font-serif-display text-[18px] md:text-[20px] py-4 hover:no-underline">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[15px] leading-[1.7] text-[var(--ri-ink-soft)] pb-5">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>

          {/* Sticky booking sidebar */}
          <aside className="lg:sticky lg:top-[92px] h-fit">
            <div className="rounded-2xl bg-[#fffdf7] border border-[var(--ri-border)] p-6 shadow-[0_20px_40px_-20px_rgba(0,0,0,.15)]">
              {isPrivateOnly && tour.private_pricing_tiers && tour.private_pricing_tiers.length > 0 ? (
                <div>
                  <p className="text-[13px] text-[var(--ri-muted)] mb-1">Pricing by group size</p>
                  <div className="space-y-1">
                    {[...tour.private_pricing_tiers]
                      .sort((a, b) => (a.min_guests || 1) - (b.min_guests || 1))
                      .map((tier, i) => {
                        const min = tier.min_guests || 1;
                        const max = tier.max_guests;
                        const label = max
                          ? min === max
                            ? `${min} guest${min > 1 ? "s" : ""}`
                            : `${min}–${max} guests`
                          : `${min}+ guests`;
                        return (
                          <div key={i} className="flex items-baseline justify-between">
                            <span className="text-[14px] text-[var(--ri-ink-soft)]">{label}</span>
                            {tier.mode === "contact" ? (
                              <span className="text-[15px] font-medium text-[var(--ri-red)]">Please contact us</span>
                            ) : tier.mode === "per_person" ? (
                              <span className="font-serif-display text-[22px] leading-none">€{tier.price}<span className="text-[13px] text-[var(--ri-muted)]"> pp</span></span>
                            ) : (
                              <span className="font-serif-display text-[22px] leading-none">€{tier.price}<span className="text-[13px] text-[var(--ri-muted)]"> total</span></span>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              ) : (
                <div className="flex items-end gap-2">
                  <span className="font-serif-display text-[36px] leading-none">
                    {isPrivateOnly ? (tour.private_price || "Contact us for pricing") : tour.price}
                  </span>
                  {!(isPrivateOnly && !tour.private_price) && (
                    <span className="text-[13px] text-[var(--ri-muted)] pb-1">{isPrivateOnly ? "for your group" : "per person"}</span>
                  )}
                </div>
              )}
              {!isPrivateOnly && tour.child_price && (
                <p className="mt-1.5 text-[13px] text-[var(--ri-muted)]">
                  Children (6–11): <span className="font-medium text-[var(--ri-ink)]">{tour.child_price}</span> · Adults 12+ pay full price
                </p>
              )}
              <div className="mt-5 space-y-3 text-[13.5px] text-[var(--ri-ink-soft)]">
                <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-[var(--ri-red)]" /> Free cancellation up to 24h</div>
                <div className="flex items-center gap-2"><Zap size={14} className="text-[var(--ri-red)]" /> Instant confirmation</div>
                <div className="flex items-center gap-2"><Users size={14} className="text-[var(--ri-red)]" /> {tour.max_guests}</div>
              </div>
              {tour.custom_request_url ? (
                <Link to={tour.custom_request_url} className="btn-primary w-full mt-6 inline-flex items-center justify-center">
                  Start your request
                </Link>
              ) : (
                <button onClick={() => setBookingOpen(true)} className="btn-primary w-full mt-6">
                  Book this tour
                </button>
              )}
              {!isPrivateOnly && tour.linked_private_tour_id && (
                <Link
                  to={`/tour/${tour.linked_private_tour_id}`}
                  className="btn-outline w-full mt-2.5 inline-flex items-center justify-center"
                >
                  Book this tour as private
                </Link>
              )}
              {!tour.custom_request_url && settings?.show_whatsapp_tour_button !== false && settings?.whatsapp_number && (
                <a
                  href={buildWhatsAppMessageUrl(
                    settings.whatsapp_number,
                    (settings.whatsapp_tour_message_template || "Hi! I'd like to book the {tour} tour.").replace(
                      "{tour}",
                      tour.title
                    )
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => fireGoogleAdsConversion(settings?.google_ads_whatsapp_send_to)}
                  className="btn-whatsapp w-full mt-3 inline-flex items-center justify-center gap-2"
                >
                  <MessageCircle size={16} /> Book with WhatsApp
                </a>
              )}
              <p className="mt-3 text-[11.5px] text-[var(--ri-muted)] text-center">
                {tour.custom_request_url
                  ? "Tell us a bit about your trip — no payment needed to start."
                  : "Reserve now, pay later via secure link."}
              </p>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="section-tight bg-[var(--ri-cream-2)]">
          <div className="container-x">
            <h2 className="font-serif-display text-[26px] md:text-[32px] mb-8">You might also like</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <TourCard key={r.id} tour={r} />
              ))}
            </div>
          </div>
        </section>
      )}

      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} tour={tour} forcePrivate={isPrivateOnly} />
    </div>
  );
};

export default TourDetailPage;
